import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/format";
import { homeForRole } from "@/constants/modules";
import { Factory, Landmark, Warehouse, FlaskConical, ShoppingCart, ShieldCheck, Truck, Mic, Loader2 } from "lucide-react";
import { toast } from "sonner";

const QUICK_ROLES = [
  { label: "Plant Operations Manager", modules: "PP · QM · EWM", email: "plant@epgroup.co.uk", pw: "demo123", icon: Factory, color: "#3B82F6" },
  { label: "Financial Controller", modules: "FI · CO · SD", email: "finance@epgroup.co.uk", pw: "demo123", icon: Landmark, color: "#10B981" },
  { label: "Warehouse Director", modules: "MM · EWM · ASRS", email: "warehouse@epgroup.co.uk", pw: "demo123", icon: Warehouse, color: "#EC4899" },
  { label: "Quality Inspector", modules: "QM · PP", email: "quality@epgroup.co.uk", pw: "demo123", icon: FlaskConical, color: "#EAB308" },
  { label: "Sales & Distribution", modules: "SD · EDI", email: "sales@epgroup.co.uk", pw: "demo123", icon: ShoppingCart, color: "#8B5CF6" },
  { label: "Warehouse Picker (Voice Picking)", modules: "PICK", email: "picker@epgroup.co.uk", pw: "demo123", icon: Mic, color: "#DB2777" },
  { label: "Delivery Driver (POD app)", modules: "ROUTES · POD", email: "driver@epgroup.co.uk", pw: "demo123", icon: Truck, color: "#0EA5E9" },
  { label: "System Administrator", modules: "ALL MODULES", email: "pt@epgroup.co.uk", pw: "admin123", icon: ShieldCheck, color: "#0a6ed1" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");

  const doLogin = async (em, pw, key) => {
    setBusy(key); setError("");
    try {
      const u = await login(em, pw);
      toast.success(`Signed in as ${u.role_label}`);
      navigate(homeForRole(u.role));
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg); toast.error(msg);
    } finally { setBusy(null); }
  };

  return (
    <div className="relative min-h-screen grid lg:grid-cols-2 bg-[#f5f6f7]">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-[#161d23] p-12">
        <div className="absolute inset-x-0 top-0 z-10 h-1.5 bg-[#D0313A]" />
        <img src="https://images.unsplash.com/photo-1610891015188-5369212db097?crop=entropy&cs=srgb&fm=jpg&q=85"
             alt="EP Group packaging production line" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#161d23]/95 via-[#161d23]/80 to-[#0f141a]/95" />

        <div className="relative flex items-center gap-4">
          <img src="/epgroup-logo-white.gif" alt="EP Group" className="h-11 w-auto" />
          <div className="h-8 w-px bg-white/20" />
          <div className="text-[11px] uppercase leading-tight tracking-[0.22em] text-white/70">Management<br />System</div>
        </div>

        <div className="relative">
          <div className="mb-6 h-1 w-14 rounded-full bg-[#D0313A]" />
          <h1 className="text-6xl font-bold leading-[0.95] tracking-tight text-white">
            Make it<br />easy<span className="text-[#D0313A]">.</span>
          </h1>
          <p className="mt-6 max-w-md text-[15px] leading-relaxed text-white/75">
            The single management system behind EP Group — innovator of packaging &amp; consumables,
            manufacturer for retail &amp; hospitality. FI · CO · PP · SD · MM · EWM · QM, unified across
            the UK, Europe, NI &amp; Export.
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            {["4 Operating Sites", "7 SAP Modules", "Retail & Hospitality", "EDI · ASRS · AI"].map((t) => (
              <span key={t} className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-[11px] font-medium tracking-wide text-white/80 backdrop-blur-sm">{t}</span>
            ))}
          </div>
        </div>

        <div className="relative flex items-center gap-2 text-xs text-white/50">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#D0313A]" />
          © 2026 EP Group · epgroup.co.uk
        </div>
      </div>

      {/* Right auth panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="mb-1 flex items-center gap-2">
            <span className="flex h-6 items-center rounded bg-[#D0313A] px-1.5 text-[11px] font-bold text-white">ep</span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6a6d70]">EP Group Management System</span>
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-[#161d23]">Sign in</h2>
          <p className="mt-1 text-sm text-[#6a6d70]">Instant role access — pick a persona to explore the full suite.</p>

          <div className="mt-6 grid gap-2.5">
            {QUICK_ROLES.map((r) => (
              <button key={r.email} data-testid={`quick-role-${r.email.split("@")[0]}`}
                disabled={!!busy}
                onClick={() => doLogin(r.email, r.pw, r.email)}
                className="group flex items-center gap-3 rounded-lg border border-[#e5e9f0] bg-white px-4 py-3 text-left shadow-sm transition-all hover:border-[#D0313A] hover:shadow-md disabled:opacity-50">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg"
                     style={{ background: `${r.color}18`, color: r.color }}><r.icon size={20} /></div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#32363a]">{r.label}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6a6d70]">{r.modules}</div>
                </div>
                {busy === r.email ? <Loader2 className="animate-spin text-[#6a6d70]" size={18} />
                  : <span className="text-[11px] font-mono text-[#6a6d70] group-hover:text-[#D0313A]">ENTER →</span>}
              </button>
            ))}
          </div>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-[#e5e9f0]" />
            <span className="text-[10px] uppercase tracking-wider text-[#6a6d70]">or sign in manually</span>
            <div className="h-px flex-1 bg-[#e5e9f0]" />
          </div>

          <form onSubmit={(e) => { e.preventDefault(); doLogin(email, password, "manual"); }} className="grid gap-3">
            <input data-testid="login-email" type="email" placeholder="email@epgroup.co.uk" value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-md border border-[#c8cfda] bg-white px-4 py-2.5 text-sm text-[#32363a] outline-none focus:border-[#D0313A]" />
            <input data-testid="login-password" type="password" placeholder="Password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-md border border-[#c8cfda] bg-white px-4 py-2.5 text-sm text-[#32363a] outline-none focus:border-[#D0313A]" />
            {error && <p data-testid="login-error" className="text-sm text-[#bb0000]">{error}</p>}
            <button data-testid="login-submit" type="submit" disabled={busy === "manual"}
              className="mt-1 flex items-center justify-center gap-2 rounded-md bg-[#D0313A] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#b3272f] disabled:opacity-60">
              {busy === "manual" && <Loader2 className="animate-spin" size={16} />} Sign in
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
